import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Archive, ArchiveRestore, ArrowRightLeft, CalendarClock, Check, History, Loader2, Sparkles, Trash2, X } from "lucide-react";
import { V } from "@/lib/vocab";
import { EXPIRE_TIMES, expiryPreview } from "@/pages/dashboard/messages/time";

/* iter384 — community indication + expiry/archive UI shared by AI Documents. */

export const shortName = (t) => {
  const n = (t?.name || "").trim();
  if (!n) return `this ${V.lc("org")}`;
  if (n.length <= 24) return n;
  const cut = n.replace(/\b(community|homeowners?|association|hoa|owners|condominium|condo|inc\.?)\b/gi, "").replace(/\s+/g, " ").trim();
  return (cut || n).split(" ").slice(0, 3).join(" ");
};

export const fmtDay = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

export const daysUntil = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  const t = new Date(); t.setHours(0, 0, 0, 0);
  return Math.round((new Date(y, m - 1, d) - t) / 86400000);
};

const VERSION_TOKENS = /\b(19|20)\d{2}\b|\b(v|ver|version|rev|revised|final|draft|updated?|new|copy|amended)\b|\(\d+\)|\bfy\d*\b/gi;
export const normalizeTitle = (s) => (s || "").replace(/\.[a-z0-9]{2,5}$/i, "").toLowerCase().replace(VERSION_TOKENS, " ")
  .replace(/[^a-z0-9]+/g, " ").split(" ").filter((w) => w.length > 1).join(" ");
export const looksLikeNewVersion = (a, b) => {
  const na = normalizeTitle(a), nb = normalizeTitle(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  const sa = new Set(na.split(" ")), sb = new Set(nb.split(" "));
  const inter = [...sa].filter((w) => sb.has(w)).length;
  return inter >= 2 && inter / new Set([...sa, ...sb]).size >= 0.75;
};

const logoUrl = (tenant) => (tenant?.brand?.logo && tenant?.id ? `${process.env.REACT_APP_BACKEND_URL}/api/public/tenant/${tenant.id}/logo` : "");

export function CommunityChip({ tenant, verb = "Teaching" }) {
  if (!tenant) return null;
  const color = tenant.brand?.color || "#e5342e";
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-bold text-ink bg-white" data-testid="community-chip"
      style={{ borderColor: `${color}55`, boxShadow: `inset 3px 0 0 ${color}` }}>
      {logoUrl(tenant) ? <img src={logoUrl(tenant)} alt="" className="h-3.5 w-3.5 rounded-sm object-contain" /> : <span className="h-2 w-2 rounded-full" style={{ background: color }} />}
      <span className="text-ink/50 font-semibold">{verb}</span> <span data-testid="community-chip-name">{shortName(tenant)}</span>
    </span>
  );
}

export function CommunityBanner({ tenant, files = [], countLabel }) {
  const color = tenant?.brand?.color || "#e5342e";
  return (
    <div data-testid="upload-community-banner" className="rounded-2xl border p-3.5 flex items-center gap-3" style={{ borderColor: `${color}66`, background: `${color}0d` }}>
      <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0 bg-white border" style={{ borderColor: `${color}55` }}>
        {logoUrl(tenant) ? <img src={logoUrl(tenant)} alt="" className="h-6 w-6 object-contain" /> : <span className="h-3 w-3 rounded-full" style={{ background: color }} />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-bold uppercase tracking-[0.14em]" style={{ color }}>Uploading to</div>
        <div className="font-heading font-extrabold text-ink text-base leading-tight truncate" data-testid="upload-community-name">{tenant?.name || `this ${V.lc("org")}`}</div>
        {countLabel && <div className="text-[11px] text-muted-foreground mt-0.5">{countLabel}</div>}
      </div>
    </div>
  );
}

export function ExpiryBadge({ item, compact = false }) {
  if (item?.archived) {
    const why = item.archived_reason === "superseded" ? "Replaced" : item.archived_reason === "expired" ? "Expired" : "Archived";
    return <span data-testid={`expiry-badge-${item.id}`} className="inline-flex items-center gap-1 rounded-full bg-gray-100 text-gray-600 px-2 py-0.5 text-[10px] font-semibold"><Archive className="h-3 w-3" /> {why}</span>;
  }
  if (item?.historical) {
    return <span data-testid={`expiry-badge-${item.id}`} className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-600 px-2 py-0.5 text-[10px] font-semibold"><History className="h-3 w-3" /> Past · expired {fmtDay(item.expires_at)}</span>;
  }
  if (!item?.expires_at) return null;
  const n = daysUntil(item.expires_at);
  const soon = n != null && n <= 30;
  const ai = item.expiry_source === "ai";
  return (
    <span data-testid={`expiry-badge-${item.id}`} title={ai ? `AI-detected: ${item.expiry_evidence || "expiration date found in the document"}` : "Set by your team"}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${soon ? "bg-amber-100 text-amber-800" : "bg-sand-100 text-ink/60"}`}>
      <CalendarClock className="h-3 w-3" />
      {compact ? (n <= 0 ? "Expires today" : n <= 30 ? `${n}d left` : fmtDay(item.expires_at))
        : (n <= 0 ? "Expires today" : n <= 30 ? `Expires in ${n} day${n === 1 ? "" : "s"}` : `Expires ${fmtDay(item.expires_at)}`)}
      {ai && <Sparkles className="h-2.5 w-2.5 opacity-70" />}
    </span>
  );
}

/* Date + optional time + "what happens" — used by the fact editor, uploads and the document preview. */
export function ExpiryField({ value, action = "archive", time = "", tz = null, onChange, disabled, suggestion, onAcceptSuggestion, onDismissSuggestion, testid = "expiry", compact = false, showTime = false }) {
  const pending = suggestion && !suggestion.dismissed && !suggestion.applied && !suggestion.none && suggestion.date && !value;
  return (
    <div data-testid={`${testid}-field`}>
      {!compact && <label className="text-sm font-semibold flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5 text-ink/50" /> Expires <span className="text-xs font-normal text-muted-foreground">(optional)</span></label>}
      <div className={`${compact ? "" : "mt-1"} flex flex-wrap items-center gap-2`}>
        <input type="date" data-testid={`${testid}-date`} value={value || ""} disabled={disabled}
          onChange={(e) => onChange({ expires_at: e.target.value || null, expiry_action: action, expire_time: e.target.value ? time : null })}
          className="rounded-xl border border-sand-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-redline-500/30 disabled:bg-sand-50" />
        {value && (
          <>
            {showTime && (
              <select data-testid={`${testid}-time`} value={time || ""} disabled={disabled}
                onChange={(e) => onChange({ expires_at: value, expiry_action: action, expire_time: e.target.value })}
                className="rounded-xl border border-sand-200 bg-white px-3 py-2 text-sm font-medium outline-none focus:ring-2 focus:ring-redline-500/30">
                {EXPIRE_TIMES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            )}
            <span className="text-xs text-muted-foreground">then</span>
            <select data-testid={`${testid}-action`} value={action || "archive"} disabled={disabled}
              onChange={(e) => onChange({ expires_at: value, expiry_action: e.target.value, expire_time: time })}
              className="rounded-xl border border-sand-200 bg-white px-3 py-2 text-sm font-medium outline-none focus:ring-2 focus:ring-redline-500/30">
              <option value="archive">Archive it</option>
              <option value="historical">Keep as historical</option>
            </select>
            <button type="button" data-testid={`${testid}-clear`} disabled={disabled} onClick={() => onChange({ expires_at: null, expiry_action: action, expire_time: null })}
              className="text-xs font-semibold text-ink/40 hover:text-redline-600">No expiry</button>
          </>
        )}
      </div>
      {value && (
        <p className="mt-1 text-[11px] text-muted-foreground" data-testid={`${testid}-preview`}>
          {showTime && <>Stops after <span className="font-semibold text-ink/80">{expiryPreview(value, time, tz)}</span> · </>}
          {action === "historical"
            ? "kept for staff and Copilot, and the concierge only ever cites it as past information."
            : `the concierge stops using it and ${V.lcs("person")} no longer see it. It's kept in the Archive — restore anytime.`}
        </p>
      )}
      {pending && (
        <div data-testid={`${testid}-suggestion`} className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 flex flex-wrap items-center gap-2">
          <Sparkles className="h-3.5 w-3.5 text-amber-600 shrink-0" />
          <span className="text-xs text-amber-900 flex-1 min-w-[160px]">
            <span className="font-bold">AI found an expiry: {fmtDay(suggestion.date)}</span>
            {suggestion.evidence && <span className="text-amber-800/80"> — “{suggestion.evidence}”</span>}
            {daysUntil(suggestion.date) < 0 && <span className="font-semibold"> · already past</span>}
          </span>
          <button type="button" data-testid={`${testid}-suggestion-accept`} disabled={disabled} onClick={() => onAcceptSuggestion?.(suggestion.date)}
            className="rounded-full bg-amber-600 text-white px-3 py-1 text-xs font-bold hover:bg-amber-700">Use it</button>
          <button type="button" data-testid={`${testid}-suggestion-dismiss`} disabled={disabled} onClick={() => onDismissSuggestion?.()}
            className="rounded-full border border-amber-300 px-3 py-1 text-xs font-bold text-amber-800 hover:bg-amber-100">No expiry</button>
        </div>
      )}
    </div>
  );
}

/* "Review N expiry suggestions" strip — backfill for documents uploaded before expiry existed. */
export function ExpirySuggestionsStrip({ tenantId, docs, readOnly, onChanged }) {
  const [open, setOpen] = useState(false);
  const [scan, setScan] = useState(null);
  const [busy, setBusy] = useState("");
  const [edit, setEdit] = useState({});
  const pending = useMemo(() => (docs || []).filter((d) => !d.archived && !d.expires_at && d.expiry_suggestion?.date && !d.expiry_suggestion.dismissed && !d.expiry_suggestion.applied), [docs]);
  const unscanned = useMemo(() => (docs || []).filter((d) => !d.archived && !d.expires_at && !d.expiry_suggestion && d.status === "indexed").length, [docs]);
  const running = scan?.status === "running";

  useEffect(() => {
    if (!running || !tenantId) return;
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/tenants/${tenantId}/documents/expiry-scan`);
        setScan(r.data.scan);
        if (r.data.scan?.status !== "running") { onChanged?.(); }
      } catch { /* keep polling */ }
    }, 2500);
    return () => clearInterval(t);
  }, [running, tenantId, onChanged]);

  const startScan = async () => {
    try {
      const r = await api.post(`/tenants/${tenantId}/documents/expiry-scan`);
      setScan(r.data.scan);
      setOpen(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't start the scan"); }
  };
  const apply = async (d, date, action = "archive") => {
    setBusy(d.id);
    try {
      await api.patch(`/tenants/${tenantId}/documents/${d.id}`, { expires_at: date, expiry_action: action });
      toast.success(`“${d.title}” expires ${fmtDay(date)}`);
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save"); }
    finally { setBusy(""); }
  };
  const ignore = async (d) => {
    setBusy(d.id);
    try { await api.patch(`/tenants/${tenantId}/documents/${d.id}`, { dismiss_expiry_suggestion: true }); onChanged?.(); }
    catch { toast.error("Couldn't dismiss"); }
    finally { setBusy(""); }
  };
  const acceptAll = async () => {
    setBusy("all");
    try {
      for (const d of pending) await api.patch(`/tenants/${tenantId}/documents/${d.id}`, { expires_at: edit[d.id] || d.expiry_suggestion.date, expiry_action: "archive" });
      toast.success(`Set expiry on ${pending.length} document${pending.length === 1 ? "" : "s"}`);
      onChanged?.(); setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save all"); }
    finally { setBusy(""); }
  };

  if (readOnly) return null;
  if (!pending.length && !unscanned && !running) return null;
  return (
    <>
      <div data-testid="expiry-strip" className={`rounded-2xl border px-4 py-3 flex flex-wrap items-center gap-3 ${pending.length ? "border-amber-200 bg-amber-50" : "border-sand-200 bg-white"}`}>
        <div className={`h-9 w-9 rounded-xl flex items-center justify-center shrink-0 ${pending.length ? "bg-amber-100" : "bg-sand-100"}`}>
          {running ? <Loader2 className="h-4 w-4 animate-spin text-ink/50" /> : <CalendarClock className={`h-4 w-4 ${pending.length ? "text-amber-700" : "text-ink/50"}`} />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-ink" data-testid="expiry-strip-title">
            {running ? `Checking documents for expiration dates… ${scan?.done ?? 0}${scan?.total ? ` of ${scan.total}` : ""}`
              : pending.length ? `Review ${pending.length} expiry suggestion${pending.length === 1 ? "" : "s"}`
              : `Check ${unscanned} document${unscanned === 1 ? "" : "s"} for expiration dates`}
          </div>
          <div className="text-xs text-muted-foreground">
            {pending.length ? "The AI found dates like policy periods and “valid through” — accept, change, or ignore each one. Nothing changes until you do."
              : "Insurance certificates, permits and seasonal schedules go stale. The AI reads each document once and proposes an expiry — you decide."}
          </div>
        </div>
        {pending.length > 0 && <button data-testid="expiry-strip-review" onClick={() => setOpen(true)} className="rounded-full bg-ink text-white text-sm font-semibold px-4 py-2 hover:bg-ink-700">Review</button>}
        {!running && unscanned > 0 && <button data-testid="expiry-strip-scan" onClick={startScan} className="rounded-full border border-sand-300 bg-white text-sm font-semibold px-4 py-2 hover:border-ink">{pending.length ? "Scan more" : "Scan documents"}</button>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="expiry-review-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><CalendarClock className="h-5 w-5 text-amber-600" /> Expiry suggestions</DialogTitle>
            <DialogDescription>On the date you accept, the document is archived — the concierge stops sharing it and {V.lcs("person")} no longer see it. You can restore it anytime.</DialogDescription>
          </DialogHeader>
          {running && <div className="text-xs text-muted-foreground flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Still scanning — {scan?.done ?? 0}{scan?.total ? ` of ${scan.total}` : ""} checked, {scan?.found ?? 0} found so far.</div>}
          {!pending.length && !running && <div className="text-sm text-muted-foreground py-6 text-center" data-testid="expiry-review-empty">Nothing waiting — every document either has an expiry or doesn't need one.</div>}
          <div className="space-y-2">
            {pending.map((d) => (
              <div key={d.id} data-testid={`expiry-row-${d.id}`} className="rounded-xl border border-sand-200 p-3 flex flex-wrap items-center gap-3">
                <div className="min-w-0 flex-1 basis-56">
                  <div className="font-semibold text-sm truncate">{d.title}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{d.expiry_suggestion.confidence === "explicit" ? "States" : "Looks like"} {d.expiry_suggestion.kind && d.expiry_suggestion.kind !== "other" ? `${d.expiry_suggestion.kind} · ` : ""}“{d.expiry_suggestion.evidence || "period found in the text"}”</div>
                </div>
                <input type="date" data-testid={`expiry-row-date-${d.id}`} value={edit[d.id] || d.expiry_suggestion.date} onChange={(e) => setEdit((m) => ({ ...m, [d.id]: e.target.value }))}
                  className="rounded-lg border border-sand-200 px-2.5 py-1.5 text-sm" />
                <button data-testid={`expiry-row-accept-${d.id}`} disabled={!!busy} onClick={() => apply(d, edit[d.id] || d.expiry_suggestion.date)}
                  className="rounded-full bg-amber-600 text-white px-3 py-1.5 text-xs font-bold hover:bg-amber-700 disabled:opacity-50 inline-flex items-center gap-1"><Check className="h-3 w-3" /> Accept</button>
                <button data-testid={`expiry-row-ignore-${d.id}`} disabled={!!busy} onClick={() => ignore(d)}
                  className="rounded-full border border-sand-300 px-3 py-1.5 text-xs font-bold text-ink/60 hover:border-ink disabled:opacity-50 inline-flex items-center gap-1"><X className="h-3 w-3" /> Ignore</button>
              </div>
            ))}
          </div>
          {pending.length > 1 && (
            <button data-testid="expiry-review-accept-all" disabled={!!busy} onClick={acceptAll}
              className="w-full rounded-full bg-ink text-white font-semibold py-2.5 hover:bg-ink-700 disabled:opacity-60">{busy === "all" ? "Saving…" : `Accept all ${pending.length}`}</button>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

/* Archive tab — everything that left the brain, with why, and the way back. */
export function ArchivePanel({ tenantId, docs, entries, readOnly, onChanged, onPreview }) {
  const [busy, setBusy] = useState("");
  const [restoring, setRestoring] = useState(null); // {item, kind}
  const [date, setDate] = useState("");
  const [action, setAction] = useState("archive");
  const items = useMemo(() => {
    const a = (docs || []).filter((d) => d.archived || d.historical).map((d) => ({ ...d, _kind: "document" }));
    const b = (entries || []).filter((e) => e.archived || e.expired || e.historical).map((e) => ({ ...e, _kind: e.kind }));
    return [...a, ...b].sort((x, y) => (y.archived_at || y.historical_since || y.expires_at || "").localeCompare(x.archived_at || x.historical_since || x.expires_at || ""));
  }, [docs, entries]);
  const why = (it) => {
    if (it.historical && !it.archived) return `Past information since ${fmtDay(it.expires_at)} — still cited as history`;
    if (it.archived_reason === "superseded") return `Replaced by a newer version${it.archived_at ? ` · ${new Date(it.archived_at).toLocaleDateString()}` : ""}`;
    if (it.archived_reason === "expired" || it.expired) return `Expired ${fmtDay(it.expires_at || it.date)}`;
    return `Archived by ${it.archived_by || "your team"}${it.archived_at ? ` · ${new Date(it.archived_at).toLocaleDateString()}` : ""}`;
  };
  const restore = async () => {
    const it = restoring.item;
    setBusy(it.id);
    try {
      const path = it._kind === "document" ? `documents/${it.id}/restore` : `knowledge/${it.id}/restore`;
      await api.post(`/tenants/${tenantId}/${path}`, { expires_at: date || null, expiry_action: action });
      toast.success(`“${it.title}” is back in the brain`, { description: date ? `Expires ${fmtDay(date)}.` : "No expiry — it stays until you archive it." });
      setRestoring(null); setDate(""); setAction("archive");
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't restore"); }
    finally { setBusy(""); }
  };
  const destroy = async (it) => {
    if (!window.confirm(`Delete “${it.title}” permanently? This can't be undone.`)) return;
    setBusy(it.id);
    try {
      await api.delete(`/tenants/${tenantId}/${it._kind === "document" ? "documents" : "knowledge"}/${it.id}`);
      toast.success("Deleted permanently");
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't delete"); }
    finally { setBusy(""); }
  };
  if (!items.length) {
    return (
      <div data-testid="archive-empty" className="bg-white rounded-3xl border border-sand-200 p-12 text-center flex flex-col items-center gap-3">
        <div className="h-12 w-12 rounded-2xl bg-sand-100 flex items-center justify-center"><Archive className="h-6 w-6 text-ink/40" strokeWidth={1.5} /></div>
        <div className="font-heading font-extrabold">Nothing archived</div>
        <p className="text-sm text-muted-foreground max-w-sm">When a document or fact expires, gets replaced by a newer version, or you archive it, it lands here — kept for your records, out of the concierge's answers.</p>
      </div>
    );
  }
  return (
    <div className="bg-white rounded-2xl border border-sand-200 divide-y divide-sand-100 overflow-hidden" data-testid="archive-list">
      {items.map((it) => (
        <div key={it.id} data-testid={`archive-row-${it.id}`} className="flex items-center gap-3 px-4 py-3">
          <div className="h-9 w-9 rounded-xl bg-sand-100 flex items-center justify-center shrink-0">
            {it.historical && !it.archived ? <History className="h-4 w-4 text-slate-500" /> : <Archive className="h-4 w-4 text-ink/50" />}
          </div>
          <div className="min-w-0 flex-1">
            <button type="button" onClick={() => it._kind === "document" && onPreview?.(it)} className={`font-semibold text-sm truncate block text-left ${it._kind === "document" ? "hover:text-redline-600" : "cursor-default"}`}>{it.title}</button>
            <div className="text-[11px] text-muted-foreground truncate">
              <span className="uppercase font-bold tracking-wide text-ink/40 mr-1.5">{it._kind}</span>{why(it)}
              {it.superseded_by && docs?.find((d) => d.id === it.superseded_by) ? ` → “${docs.find((d) => d.id === it.superseded_by).title}”` : ""}
            </div>
          </div>
          {!readOnly && (
            <>
              <button data-testid={`archive-restore-${it.id}`} disabled={!!busy} onClick={() => { setRestoring({ item: it }); setDate(""); setAction("archive"); }}
                className="inline-flex items-center gap-1 rounded-full border border-sand-300 px-3 py-1.5 text-xs font-bold hover:border-ink disabled:opacity-50"><ArchiveRestore className="h-3.5 w-3.5" /> Restore</button>
              <button data-testid={`archive-delete-${it.id}`} disabled={!!busy} onClick={() => destroy(it)} title="Delete permanently"
                className="text-ink/30 hover:text-danger-600 disabled:opacity-50"><Trash2 className="h-4 w-4" /></button>
            </>
          )}
        </div>
      ))}
      <Dialog open={!!restoring} onOpenChange={(v) => !v && setRestoring(null)}>
        <DialogContent className="max-w-md" data-testid="restore-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Restore “{restoring?.item?.title}”</DialogTitle>
            <DialogDescription>It goes straight back into the concierge's answers. Give it a new expiry if it's still time-limited.</DialogDescription>
          </DialogHeader>
          <ExpiryField value={date} action={action} onChange={(p) => { setDate(p.expires_at || ""); setAction(p.expiry_action || "archive"); }} testid="restore-expiry" />
          <button data-testid="restore-confirm" disabled={!!busy} onClick={restore}
            className="w-full rounded-full bg-redline-500 text-white font-semibold py-3 hover:bg-redline-600 disabled:opacity-60">{busy ? "Restoring…" : date ? `Restore · expires ${fmtDay(date)}` : "Restore with no expiry"}</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* Move a document or fact that landed in the wrong community. */
export function MoveToCommunityDialog({ open, onClose, tenants, currentId, item, kind, tenantId, onMoved }) {
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const others = (tenants || []).filter((t) => t.id !== currentId);
  useEffect(() => { if (open) setTarget(""); }, [open]);
  const go = async () => {
    setBusy(true);
    try {
      const path = kind === "document" ? `documents/${item.id}/move` : `knowledge/${item.id}/move`;
      const r = await api.post(`/tenants/${tenantId}/${path}`, { target_tenant_id: target });
      toast.success(`Moved to ${r.data.tenant_name || `the other ${V.lc("org")}`}`, { description: `“${item.title}” now lives there — indexed and ready.` });
      onMoved?.(); onClose?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't move"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="move-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><ArrowRightLeft className="h-5 w-5 text-redline-500" /> Move to another {V.lc("org")}</DialogTitle>
          <DialogDescription>“{item?.title}” leaves this {V.lc("org")} entirely — file, summary, tags and index move with it. Nothing to re-upload.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="move-targets">
          {others.map((t) => (
            <button key={t.id} type="button" data-testid={`move-target-${t.id}`} onClick={() => setTarget(t.id)}
              className={`w-full text-left rounded-xl border px-4 py-2.5 text-sm font-semibold transition-colors flex items-center gap-2 ${target === t.id ? "border-redline-500 bg-redline-50 text-redline-600" : "border-sand-200 hover:border-ink"}`}>
              <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: t.brand?.color || "#e5342e" }} />{t.name}
            </button>
          ))}
          {!others.length && <div className="text-sm text-muted-foreground">You only have access to this {V.lc("org")}.</div>}
        </div>
        <button data-testid="move-confirm" disabled={!target || busy} onClick={go}
          className="w-full rounded-full bg-redline-500 text-white font-semibold py-3 hover:bg-redline-600 disabled:opacity-50">{busy ? "Moving…" : target ? `Move to ${others.find((t) => t.id === target)?.name}` : `Pick ${V.a("org")}`}</button>
      </DialogContent>
    </Dialog>
  );
}

export const toastAdded = (what, community, onUndo) =>
  toast.success(`${what} added to ${community}`, { duration: 10000, description: `Wrong ${V.lc("org")}? Undo takes it right back out.`, action: { label: "Undo", onClick: onUndo } });
